import {type CopilotChatModel, CopilotPlan, type RequiredPlan} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {FIRST_PARTY_VENDORS} from './model-vendors'
import {isCustomModel} from './models'
import {RECOMMENDED_MODELS, type RecommendedModelEntry} from './recommended-chat-models'

/**
 * Represents a group of custom models with a display name and owner type
 */
export interface CustomModelGroup {
  displayName: string
  ownerType?: 'organization' | 'enterprise'
  models: CopilotChatModel[]
}

/**
 * Model tier configuration
 */
export interface ModelTierConfig {
  lowTier: CopilotChatModel[]
  midTier: CopilotChatModel[]
  highTier: CopilotChatModel[]
  base: CopilotChatModel[]
  preview: CopilotChatModel[]
  custom: {[groupKey: string]: CustomModelGroup}
}

const AUTO_MODEL_ID = 'auto'

// plans allowed to access a model at each requiredPlan tier.
const RESTRICTED_TO_PLANS: Readonly<Record<RequiredPlan, CopilotPlan[]>> = {
  business: [CopilotPlan.Business, CopilotPlan.Enterprise],
  max: [CopilotPlan.IndividualMax, CopilotPlan.Business, CopilotPlan.Enterprise],
  // eslint-disable-next-line camelcase
  pro_plus: [CopilotPlan.IndividualProPlus, CopilotPlan.Business, CopilotPlan.Enterprise, CopilotPlan.IndividualMax],
  pro: [
    CopilotPlan.IndividualPro,
    CopilotPlan.IndividualProPlus,
    CopilotPlan.Business,
    CopilotPlan.Enterprise,
    CopilotPlan.IndividualMax,
  ],
  edu: [
    CopilotPlan.IndividualStudent,
    CopilotPlan.IndividualPro,
    CopilotPlan.IndividualProPlus,
    CopilotPlan.Business,
    CopilotPlan.Enterprise,
    CopilotPlan.IndividualMax,
  ],
}

function getRestrictedToPlans(requiredPlan?: RequiredPlan): CopilotPlan[] | undefined {
  return requiredPlan ? RESTRICTED_TO_PLANS[requiredPlan] : undefined
}

// individual-plan tiers ordered from least to most privileged. Used to infer access to a CAPI-returned model
const INDIVIDUAL_PLAN_TIERS_ASCENDING: readonly CopilotPlan[] = [
  CopilotPlan.IndividualStudent,
  CopilotPlan.IndividualPro,
  CopilotPlan.IndividualProPlus,
  CopilotPlan.IndividualMax,
]

// `business` is intentionally omitted: there's no individual self-serve upgrade path to Business,
const MIN_PLAN_FOR_REQUIRED_PLAN: Readonly<Partial<Record<RequiredPlan, CopilotPlan>>> = {
  edu: CopilotPlan.IndividualStudent,
  pro: CopilotPlan.IndividualPro,
  // eslint-disable-next-line camelcase
  pro_plus: CopilotPlan.IndividualProPlus,
  max: CopilotPlan.IndividualMax,
}

/**
 * Returns the minimum individual plan that can access `model`, or undefined if the
 * model is unrestricted or only available to org plans (Business/Enterprise). For locked
 * placeholders created from a recommended-models entry this comes from `requiredPlan`;
 * for real CAPI models it's the lowest-tier individual plan listed in `restricted_to`.
 */
export function getMinimumRequiredPlan(model: CopilotChatModel): CopilotPlan | undefined {
  if (model.requiredPlan) {
    return MIN_PLAN_FOR_REQUIRED_PLAN[model.requiredPlan]
  }
  const restricted = model.billing?.restricted_to
  if (!restricted?.length) return undefined
  return INDIVIDUAL_PLAN_TIERS_ASCENDING.find(plan => restricted.includes(plan))
}

/**
 * Returns true if the given plan is entitled to access a model with the given requiredPlan.
 * Used to detect retired models: if a user is entitled but CAPI didn't return the model,
 * it has likely been retired and should be hidden rather than shown as a selectable placeholder.
 */
export function isUserEntitled(plan: CopilotPlan, requiredPlan?: RequiredPlan): boolean {
  const restrictedTo = getRestrictedToPlans(requiredPlan)
  return !restrictedTo || restrictedTo.includes(plan)
}

/**
 * Creates a locked placeholder CopilotChatModel for a recommended model that
 * CAPI did not return for the current user (e.g. free plan or logged-out).
 * The placeholder's billing.restricted_to mirrors production data so that
 * ModelListItem computes the correct lock state and tooltip.
 */
function createPlaceholderModel(entry: RecommendedModelEntry): CopilotChatModel {
  const restrictedTo = getRestrictedToPlans(entry.requiredPlan)

  return {
    id: entry.id,
    name: entry.displayName,
    displayName: entry.displayName,
    logoURL: entry.logoURL,
    version: entry.id,
    vendor: entry.vendor,
    preview: false,
    // eslint-disable-next-line camelcase
    model_picker_enabled: true,
    // eslint-disable-next-line camelcase
    model_picker_category: entry.category,
    hasLimitedCapabilities: false,
    isThirdParty: !FIRST_PARTY_VENDORS.has(entry.vendor),
    capabilities: {
      family: entry.id,
      // eslint-disable-next-line camelcase
      limits: {max_prompt_tokens: 128000},
      // eslint-disable-next-line camelcase
      supports: {tool_calls: true, streaming: true},
      tokenizer: 'o200k_base',
      type: 'chat',
    },
    billing: {
      // eslint-disable-next-line camelcase
      is_premium: !!restrictedTo,
      multiplier: 0,
      // eslint-disable-next-line camelcase
      ...(restrictedTo && {restricted_to: restrictedTo}),
    },
    requiredPlan: entry.requiredPlan,
  }
}

/**
 * Low tier model exceptions - models that should be in low tier despite billing multiplier
 */
const LOW_TIER_MODEL_EXCEPTIONS = ['o3-mini', 'Gemini 2.0 Flash']

/**
 * Determines if a model belongs to the low tier based on category or billing
 */
function isLowTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'lightweight'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      ((model.billing?.multiplier < 1 && model.billing?.multiplier !== 0) ||
        LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName))) ||
    false
  )
}

/**
 * Determines if a model belongs to the mid tier based on category or billing
 */
function isMidTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'versatile'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      (model.billing?.multiplier === 1 || model.billing?.multiplier === 0) &&
      !LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName)) ||
    false
  )
}

/**
 * Determines if a model belongs to the high tier based on category or billing
 */
function isHighTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'powerful'
  }

  // Fallback to billing multiplier logic
  return (model.billing && model.billing?.multiplier > 1) || false
}

/**
 * Checks if models have diversity in their categories (either by category field or billing)
 */
export function hasModelCategoryDiversity(models: CopilotChatModel[]): boolean {
  // Check for category diversity or billing diversity as fallback
  const categoryDiversity =
    models.some(m => m.model_picker_category !== undefined) &&
    new Set(models.filter(m => m.model_picker_category !== undefined).map(m => m.model_picker_category)).size > 1

  const billingDiversity =
    models.some(m => m.billing?.multiplier !== undefined) &&
    new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1

  return categoryDiversity || billingDiversity
}

/**
 * Creates a unique group key for custom models based on owner name and type.
 * This ensures enterprise and org models with the same name are in separate groups.
 */
function getCustomModelGroupKey(model: CopilotChatModel): string {
  const ownerName = model.custom_model!.owner_name
  const ownerType = model.custom_model!.owner_type ?? 'organization'
  return `${ownerName}::${ownerType}`
}

export interface CategorizeModelsOptions {
  shouldShowCategories: boolean
  shouldShowPreviewModelsSeparately?: boolean
  recommendedOnly?: boolean
  userPlan?: CopilotPlan
}

/**
 * Adds "(enterprise)" or "(organization)" suffix only when there are two with the same name.
 */
function getDisplayName(
  ownerName: string,
  ownerType: 'organization' | 'enterprise' | undefined,
  allGroups: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}},
): string {
  // Check if there's another group with the same owner name but different owner type
  const hasNameCollision = Object.values(allGroups).some(
    group =>
      group.ownerType !== ownerType &&
      group.ownerName.trim().toLocaleLowerCase() === ownerName.trim().toLocaleLowerCase(),
  )

  if (hasNameCollision && ownerType) {
    return `${ownerName} (${ownerType})`
  }
  return ownerName
}

/**
 * Categorizes models into tiers for the model picker
 */
export function categorizeModels(models: CopilotChatModel[], options: CategorizeModelsOptions): ModelTierConfig {
  const {shouldShowCategories, shouldShowPreviewModelsSeparately = true, recommendedOnly = false, userPlan} = options

  const [customModels, defaultModels] = models.reduce<[CopilotChatModel[], CopilotChatModel[]]>(
    (acc, m) => {
      acc[isCustomModel(m) ? 0 : 1].push(m)
      return acc
    },
    [[], []],
  )

  const groupInfo: {[groupKey: string]: {ownerName: string; ownerType?: 'organization' | 'enterprise'}} = {}
  for (const m of customModels) {
    const groupKey = getCustomModelGroupKey(m)
    if (!groupInfo[groupKey]) {
      groupInfo[groupKey] = {
        ownerName: m.custom_model!.owner_name,
        ownerType: m.custom_model!.owner_type,
      }
    }
  }

  const customModelGroups = customModels.reduce(
    (acc, m) => {
      const groupKey = getCustomModelGroupKey(m)
      if (!acc[groupKey]) {
        const ownerName = m.custom_model!.owner_name
        const ownerType = m.custom_model!.owner_type
        acc[groupKey] = {
          displayName: getDisplayName(ownerName, ownerType, groupInfo),
          ownerType,
          models: [],
        }
      }
      acc[groupKey].models.push(m)
      return acc
    },
    {} as {[groupKey: string]: CustomModelGroup},
  )
  if (!shouldShowCategories) {
    return {
      lowTier: [],
      midTier: [],
      highTier: [],
      base: shouldShowPreviewModelsSeparately ? defaultModels.filter(m => !m.preview) : defaultModels,
      preview: shouldShowPreviewModelsSeparately ? defaultModels.filter(m => m.preview) : [],
      custom: customModelGroups,
    }
  }

  if (recommendedOnly) {
    const apiModelsById = new Map(defaultModels.map(m => [m.id.toLowerCase(), m]))
    const lightTier: CopilotChatModel[] = []
    const midTier: CopilotChatModel[] = []
    const highTier: CopilotChatModel[] = []
    const maxUpsellEnabled = copilotFeatureFlags.maxUpsellEnabled
    const isEntryUpsellable = (entry: RecommendedModelEntry): boolean =>
      entry.upsellable && (entry.requiredPlan !== 'max' || maxUpsellEnabled)

    for (const entry of RECOMMENDED_MODELS) {
      const real = apiModelsById.get(entry.id.toLowerCase())
      const entitled = !!userPlan && isUserEntitled(userPlan, entry.requiredPlan)
      const upsellable = isEntryUpsellable(entry)

      if (!real) {
        // user is entitled but CAPI didn't return the model (assumed retired)
        if (entitled) continue
        // not in CAPI for this user, and we don't want to upsell it
        if (!upsellable) continue
      } else if (!upsellable && userPlan && !entitled) {
        // CAPI returned it (e.g. via enterprise context) but user's plan doesn't entitle them
        continue
      }

      // real model, or a locked placeholder for upsell.
      const model = real ?? createPlaceholderModel(entry)
      const category = real?.model_picker_category ?? entry.category

      if (category === 'lightweight') lightTier.push(model)
      else if (category === 'versatile') midTier.push(model)
      else highTier.push(model)
    }

    return {lowTier: lightTier, midTier, highTier, base: [], preview: [], custom: customModelGroups}
  }

  // If Auto Model exists (CCA), show it in the `base` section instead of categorizing it.
  const isAutoModel = (m: CopilotChatModel): boolean => m.id === AUTO_MODEL_ID
  const baseModels = defaultModels.filter(m => isAutoModel(m))
  const categorizableModels = defaultModels.filter(m => !isAutoModel(m))

  return {
    lowTier: categorizableModels.filter(m => isLowTierModel(m)),
    midTier: categorizableModels.filter(m => isMidTierModel(m)),
    highTier: categorizableModels.filter(m => isHighTierModel(m)),
    base: baseModels,
    preview: [],
    custom: customModelGroups,
  }
}
